import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import { CheckIcon } from 'lucide-react';
const BASE_PRICE = 490;
const BUMP_PRICE = 290;
const bumps = [{
  id: 'protocolo',
  emoji: '🚀',
  headline: '¡SÍ! Activar mi "Protocolo de 7 Días" para resultados rápidos.',
  priceLabel: `Añadir por solo $${BUMP_PRICE}`,
  image: 'https://cdn.magicpatterns.com/uploads/iSQWY87umZirKrkag3Gn1A/Protocolo_de_7_dias.png',
  body: 'No pierdas tiempo adivinando por cuál de las 300 recetas empezar. Con este Protocolo de 7 Días, Sofia te lleva de la mano con un calendario exacto: qué tomar al despertar, almorzar y cenar para limpiar tu cuerpo y recuperar tu energía desde la primera semana.',
  bullets: ['Calendario diario "Paso a Paso".', 'Lista de compras express para el mercado.', 'Guía de los 3 alimentos que bloquean tu sanación.']
}, {
  id: 'seguridad',
  emoji: '🛡️',
  headline: '¡AÑADIR SEGURIDAD! Guía de Interacciones y Contraindicaciones.',
  priceLabel: `Paz mental total por solo $${BUMP_PRICE}`,
  image: 'https://cdn.magicpatterns.com/uploads/pz6PPL92XTCjouZ8thSjin/Guia_de_Seguridad_Blindaje_Ancestral.png',
  body: 'Usa la medicina ancestral con responsabilidad y total paz mental. Descubre qué plantas NUNCA debes mezclar con tus medicamentos actuales (presión, azúcar o colesterol) y conoce las dosis críticas para tu seguridad.',
  bullets: ['Semáforo de interacciones (Seguro vs. Peligroso).', 'Diccionario A-Z de contraindicaciones por receta.', 'Guía de dosis máximas recomendadas.']
}];
export function PriceSection() {
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [flashId, setFlashId] = useState<string | null>(null);
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  const minutes = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const seconds = (timeLeft % 60).toString().padStart(2, '0');
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const totalPrice = BASE_PRICE + selectedCount * BUMP_PRICE;
  const toggleBump = useCallback((id: string) => {
    setSelected(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
    setFlashId(id);
    setTimeout(() => setFlashId(null), 600);
  }, []);
  return <section className="bg-ancestral-green py-14 md:py-20 text-ancestral-cream relative" id="oferta" data-id="element-159">
      <div className="container mx-auto px-4 relative z-10" data-id="element-160">
        <div className="max-w-lg mx-auto" data-id="element-161">
          {/* Section header */}
          <FadeIn data-id="element-162">
            <div className="text-center mb-6" data-id="element-163">
              <p className="text-xs uppercase tracking-[0.2em] text-ancestral-gold/70 font-semibold mb-2" data-id="element-164">
                Oferta exclusiva por tiempo limitado
              </p>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-ancestral-gold" data-id="element-165">
                Tu Recetario Ancestral + 10 Bonos
              </h2>
            </div>
          </FadeIn>

          {/* Single unified card */}
          <FadeIn delay={0.15} data-id="element-166">
            <div className="bg-black/20 border border-ancestral-gold/25 rounded-2xl overflow-hidden shadow-2xl" data-id="element-167">
              {/* Product image */}
              <div className="p-6 pb-0" data-id="element-168">
                <div className="max-w-[320px] mx-auto" data-id="element-169">
                  <img src="https://cdn.magicpatterns.com/uploads/8qwq7iJH71X98zmPKkuWDH/WhatsApp_Image_2026-03-16_at_20.07.50.jpg" alt="El Gran Recetario Ancestral" className="w-full rounded-xl shadow-lg border border-ancestral-gold/15" data-id="element-170" />
                </div>
              </div>

              {/* All hooks inside the card */}
              <div className="p-6 pt-5 space-y-5" data-id="element-171">
                {/* Countdown */}
                <div className="text-center" data-id="element-172">
                  <p className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-2" data-id="element-173">
                    ⏰ Esta oferta expira en
                  </p>
                  <div className="flex items-center justify-center gap-2.5" data-id="element-174">
                    <div className="bg-ancestral-cream/10 border border-ancestral-gold/20 rounded-lg px-4 py-2 text-center min-w-[64px]" data-id="element-175">
                      <span className="text-3xl font-bold text-ancestral-cream tabular-nums" data-id="element-176">
                        {minutes}
                      </span>
                      <p className="text-[9px] uppercase tracking-widest text-ancestral-gold/50" data-id="element-177">
                        Min
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-ancestral-gold/30" data-id="element-178">
                      :
                    </span>
                    <div className="bg-ancestral-cream/10 border border-ancestral-gold/20 rounded-lg px-4 py-2 text-center min-w-[64px]" data-id="element-179">
                      <span className="text-3xl font-bold text-ancestral-cream tabular-nums" data-id="element-180">
                        {seconds}
                      </span>
                      <p className="text-[9px] uppercase tracking-widest text-ancestral-gold/50" data-id="element-181">
                        Seg
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-ancestral-gold/10" data-id="element-182" />

                {/* Dynamic Price */}
                <div className="text-center" data-id="element-183">
                  <div className="inline-block bg-ancestral-gold text-ancestral-green font-bold px-4 py-1 rounded-full text-xs tracking-widest uppercase mb-3" data-id="element-184">
                    50% DE DESCUENTO
                  </div>
                  <div className="mb-1" data-id="element-185">
                    <span className="text-sm text-ancestral-cream/50 uppercase tracking-wider" data-id="element-186">
                      Precio normal:{' '}
                    </span>
                    <span className="text-lg font-bold text-red-400 line-through ml-1" data-id="element-187">
                      $980 UYU
                    </span>
                  </div>
                  <p className="text-ancestral-gold font-bold uppercase tracking-widest text-xs mb-1" data-id="element-188">
                    HOY SOLO:
                  </p>
                  <div className="flex items-baseline justify-center gap-1.5" data-id="element-189">
                    <motion.span key={totalPrice} initial={{
                    scale: 1.15,
                    color: '#4ade80'
                  }} animate={{
                    scale: 1,
                    color: '#C9A84C'
                  }} transition={{
                    duration: 0.4
                  }} className="text-5xl md:text-6xl font-serif font-bold drop-shadow-lg" data-id="element-190">
                      ${totalPrice.toLocaleString()}
                    </motion.span>
                    <span className="text-xl font-bold text-ancestral-gold" data-id="element-191">
                      UYU
                    </span>
                  </div>
                  {selectedCount > 0 && <motion.p initial={{
                  opacity: 0,
                  y: -5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-[10px] text-emerald-400 mt-1 font-semibold" data-id="element-192">
                      Recetario $490 + {selectedCount} bump
                      {selectedCount > 1 ? 's' : ''} × $290
                    </motion.p>}
                  <p className="text-[10px] text-ancestral-cream/35 mt-1" data-id="element-193">
                    Pesos uruguayos
                  </p>
                </div>

                <div className="border-t border-ancestral-gold/10" data-id="element-194" />

                {/* Social proof row */}
                <div className="flex items-center justify-center gap-5" data-id="element-195">
                  <div className="flex items-center gap-1.5" data-id="element-196">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-ancestral-gold" data-id="element-197">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" data-id="element-198" />
                      <polyline points="7 10 12 15 17 10" data-id="element-199" />
                      <line x1="12" y1="15" x2="12" y2="3" data-id="element-200" />
                    </svg>
                    <span className="text-xs font-semibold text-ancestral-cream/70" data-id="element-201">
                      +3,454 descargas
                    </span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-202">
                    <div className="flex gap-0.5" data-id="element-203">
                      {[1, 2, 3, 4].map(i => <svg key={i} viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-204">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-205" />
                        </svg>)}
                      <svg viewBox="0 0 20 20" className="w-3.5 h-3.5" data-id="element-206">
                        <defs data-id="element-207">
                          <linearGradient id="hs" data-id="element-208">
                            <stop offset="50%" stopColor="#C9A84C" data-id="element-209" />
                            <stop offset="50%" stopColor="#C9A84C" stopOpacity="0.3" data-id="element-210" />
                          </linearGradient>
                        </defs>
                        <path fill="url(#hs)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-211" />
                      </svg>
                    </div>
                    <span className="text-xs font-semibold text-ancestral-cream/70" data-id="element-212">
                      4.5/5
                    </span>
                  </div>
                </div>

                <div className="border-t border-ancestral-gold/10" data-id="element-213" />

                {/* CTA Button */}
                <a href="https://www.alivionaturalshop.com/cart/51513616499009:1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full bg-emerald-500 hover:bg-emerald-400 text-white border-2 border-emerald-400 rounded-xl py-4 px-6 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-214">
                  <span className="font-serif font-bold text-base md:text-lg text-center uppercase tracking-wide" data-id="element-215">
                    Quiero Mi Recetario Ahora →
                  </span>
                </a>

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-216">
                  <div className="flex items-center gap-1.5 text-ancestral-cream/60" data-id="element-217">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-218">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" data-id="element-219" />
                    </svg>
                    <span className="text-[11px] font-medium" data-id="element-220">
                      Acceso inmediato
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-ancestral-cream/60" data-id="element-221">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-222">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-223" />
                      <path d="M7 11V7a5 5 0 0110 0v4" data-id="element-224" />
                    </svg>
                    <span className="text-[11px] font-medium" data-id="element-225">
                      Pago 100% seguro
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-ancestral-cream/60" data-id="element-226">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-227">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" data-id="element-228" />
                      <line x1="12" y1="18" x2="12.01" y2="18" data-id="element-229" />
                    </svg>
                    <span className="text-[11px] font-medium" data-id="element-230">
                      Cualquier dispositivo
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* ORDER BUMPS */}
          <FadeIn delay={0.3} data-id="element-231">
            <div className="mt-6 space-y-4" data-id="element-232">
              {/* Urgency badge */}
              <p className="text-center text-xs text-ancestral-gold font-semibold" data-id="element-233">
                🔥 Oferta exclusiva de esta página:{' '}
                <span className="text-ancestral-cream" data-id="element-234">
                  87% de los clientes añaden ambos
                </span>
              </p>

              {bumps.map(bump => {
              const isSelected = !!selected[bump.id];
              const isFlashing = flashId === bump.id;
              return <motion.div key={bump.id} onClick={() => toggleBump(bump.id)} whileHover={{
                scale: 1.01,
                boxShadow: '0 0 20px rgba(201,168,76,0.25)'
              }} whileTap={{
                scale: 0.99
              }} className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-4 transition-colors duration-300 overflow-hidden ${isSelected ? 'border-ancestral-gold bg-[#FFF8F0]' : 'border-ancestral-green/50 bg-[#FFF8F0]/90 hover:border-ancestral-gold/60'}`} data-id="element-235">
                    {/* Flash overlay */}
                    {isFlashing && <motion.div initial={{
                  opacity: 0.6
                }} animate={{
                  opacity: 0
                }} transition={{
                  duration: 0.6
                }} className="absolute inset-0 bg-ancestral-gold/30 rounded-2xl pointer-events-none z-10" data-id="element-236" />}

                    {/* Header row */}
                    <div className="flex items-start gap-3 mb-3" data-id="element-237">
                      {/* Checkbox */}
                      <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center mt-0.5 transition-all duration-200 ${isSelected ? 'bg-ancestral-green border-ancestral-green' : 'border-gray-400 bg-white'}`} data-id="element-238">
                        {isSelected && <CheckIcon className="w-4 h-4 text-white" data-id="element-239" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-240">
                        <p className="font-bold text-sm text-ancestral-dark leading-snug" data-id="element-241">
                          {bump.emoji} {bump.headline}
                        </p>
                        <p className="text-xs font-bold text-emerald-600 mt-0.5" data-id="element-242">
                          ({bump.priceLabel})
                        </p>
                      </div>
                    </div>

                    {/* Image */}
                    <div className="max-w-[180px] mx-auto mb-3" data-id="element-243">
                      <img src={bump.image} alt={bump.headline} className="w-full rounded-lg shadow-md" data-id="element-244" />
                    </div>

                    {/* Body */}
                    <p className="text-xs text-ancestral-dark/70 leading-relaxed mb-2.5" data-id="element-245">
                      {bump.body}
                    </p>

                    {/* Bullets */}
                    <ul className="space-y-1" data-id="element-246">
                      {bump.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 text-xs text-ancestral-dark/80" data-id="element-247">
                          <span className="text-emerald-600 flex-shrink-0" data-id="element-248">
                            ✅
                          </span>
                          <span data-id="element-249">{bullet}</span>
                        </li>)}
                    </ul>
                  </motion.div>;
            })}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}